<template>
  <div class="mx-auto" :style="!isLocked ? 'max-width:675px' : null">

    <div v-if="!isLocked" class="mb-1" style="font-size:1.4rem">
      {{ $t('page.manageFiling.abbreviatedFiling.filingDetails.title') }}
    </div>

    <template v-if="!isLocked">
      <v-card class="mb-3 py-2 px-3">
        <breadcrumbs v-model="stepID" :steps="steps"/>
      </v-card>
    </template>

    <validation-error-panel/>

    <workflow-wrapper>
      <step-details v-if="stepID === 'details'"
                    key="details"
                    :originalBean="bean"
                    :editing="isReadyForSubmission"
                    @save="saveDetails"
                    @cancel="cancel"/>
      <step-summary v-else-if="stepID === 'summary'"
                    key="summary"
                    :originalBean="bean"
                    @edit="gotoDetails"
                    @submitFiling="$emit('submitFiling')"/>
    </workflow-wrapper>
  </div>
</template>

<script>
import _ from 'lodash';
import WorkflowWrapper from '@components/support/WorkflowWrapper';
import StepSummary from '@components/support/manageFiling/abbreviated/StepSummary';
import Breadcrumbs from '@components/support/Breadcrumbs';
import ValidationErrorPanel from '@components/support/ValidationErrorPanel';
import StepDetails from '@components/support/manageFiling/abbreviated/StepDetails';
import Filing from '@/application/filing';
import { useFilingStore } from '@/stores/filingStore';

const filingStore = useFilingStore();

export default {
  components: {
    StepDetails, WorkflowWrapper, StepSummary, Breadcrumbs, ValidationErrorPanel },
  data () {
    return {
      bean: null,
      stepID: 'details',
      steps: [
        { stepID: 'details', name: this.$t('page.manageFiling.abbreviatedFiling.steps.docketEntryDetails') },
        { stepID: 'summary', name: this.$t('page.manageFiling.abbreviatedFiling.steps.summary') }
      ]
    };
  },
  watch: {
    isLocked (newValue) {
      if (newValue === true)
      {
        this.gotoSummary();
      }
    }
  },
  computed: {
    filingID () {
      return filingStore.filingID;
    },
    filing () {
      return filingStore.filing;
    },
    leadDocketEntry () {
      return filingStore.getLeadDocketEntry;
    },
    isReadyForSubmission () {
      return Filing.isAbbreviatedFilingReadyForSubmission(this.filing);
    },
    isLocked () {
      return filingStore.isLocked;
    }
  },
  created () {
    // bean must be initialized here (not in data) since computed fields aren't available until this point
    this.bean = this.generateFormBean();
    this.start();
  },
  methods: {
    start () {
      if (this.isReadyForSubmission)
      {
        this.gotoSummary();
      }
      else
      {
        this.gotoDetails();
      }
    },
    generateFormBean () {
      return {
        filing: {
          filingNote: _.get(this.filing, 'filingNote', null),
          feeWaiverReasonID: _.get(this.filing, 'feeWaiverReason.resourceID', null)
        },
        docketEntry: {
          docketEntryNote: _.get(this.filing, 'leadDocketEntry.docketEntryNote', null)
        },
        docketEntryDocument: {
          isNew: false,
          docketEntryDocumentID: _.get(this.filing, 'leadDocketEntry.documents[0].resourceID', null),
          documentLinkID: _.get(this.filing, 'leadDocketEntry.documents[0].documentLinkID', null),
          documentName: this.leadDocketEntry.docketEntryExternalName
        }
      };
    },
    gotoDetails () {
      this.stepID = 'details';
    },
    gotoSummary () {
      this.stepID = 'summary';
    },
    saveDetails (bean) {
      this.$cove.blockNow();

      Filing.formatAbbreviatedFilingCustomFieldsMap(bean);

      this.bean = bean;

      let filingPromise = Filing.patchFilingInfo({
        filingID: this.filingID,
        payload: {
          filingNote: this.bean.filing.filingNote,
          feeWaiverReasonID: this.bean.filing.feeWaiverReasonID,
          customFieldsMap: this.bean.filing.customFieldsMap
        }
      });

      let docketEntryPromise = Filing.patchDocketEntry({
        docketEntryID: this.filing.leadDocketEntry.resourceID,
        payload: {
          docketEntryNote: this.bean.docketEntry.docketEntryNote,
          customFieldsMap: this.bean.docketEntry.customFieldsMap
        }
      });

      let docketEntryDocumentPromise = Promise.resolve();
      if (this.bean.docketEntryDocument.isNew)
      {
        docketEntryDocumentPromise =
          Filing.saveNewDocketEntryDocument({
            docketEntryID: this.leadDocketEntry.resourceID,
            bean: {
              documentName: this.bean.docketEntryDocument.documentName,
              documentLinkID: this.bean.docketEntryDocument.documentLinkID,
              customFieldsMap: this.bean.docketEntryDocument.customFieldsMap
            }
          })
            .then((response) => {
              this.bean.docketEntryDocument.docketEntryDocumentID = response.data.resourceID;
              this.bean.docketEntryDocument.isNew = false;
            });
      }

      Promise.all([filingPromise, docketEntryPromise, docketEntryDocumentPromise])
        .then(() => {
          this.$cove.notify({ message: this.$t('application.save.success'), color: 'success' });
          // have to do this reload here in order for the submission validation checks to work properly
          return filingStore.loadFiling();
        })
        .then(() => {
          this.gotoSummary();
        })
        .finally(() => {
          this.$cove.unblock();
        });
    },
    cancel () {
      this.$cove.confirm({ message: this.$t('global.confirmation.cancel.create') })
        .then((confirm) => {
          if (confirm)
          {
            this.start();
          }
        });
    }
  }
};
</script>